import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  TextField,
  Alert,
  Divider,
} from '@mui/material';
import { motion } from 'framer-motion';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { saveContact } from '../../services/storage';
import { useLanguage } from '../../contexts/LanguageContext';
import SEOHead from '../../components/SEOHead';

const MotionBox = motion(Box);

// ── Tool definitions ────────────────────────────────────────────────────────
interface Tool {
  name: string;
  color: string;
  image: string;
  description: { de: string; en: string; ar: string };
  useCases: { de: string[]; en: string[]; ar: string[] };
}

const TOOLS: Tool[] = [
  {
    name: 'Power BI',
    color: '#F2C811',
    image: '/new_images/AdobeStock_267386573.jpeg',
    description: {
      de: 'Verwandeln Sie Rohdaten in interaktive Dashboards und Berichte. Treffen Sie fundierte Entscheidungen in Echtzeit.',
      en: 'Transform raw data into interactive dashboards and reports. Make informed decisions in real time.',
      ar: 'حوّل البيانات الخام إلى لوحات تحكم وتقارير تفاعلية. اتخذ قرارات مستنيرة في الوقت الفعلي.',
    },
    useCases: {
      de: ['Umsatz-Dashboards', 'KPI-Tracking', 'Automatisierte Reports', 'Datenvisualisierung'],
      en: ['Revenue dashboards', 'KPI tracking', 'Automated reports', 'Data visualization'],
      ar: ['لوحات الإيرادات', 'تتبع مؤشرات الأداء', 'تقارير آلية', 'تصور البيانات'],
    },
  },
  {
    name: 'Power Apps',
    color: '#742774',
    image: '/new_images/AdobeStock_986081604.jpeg',
    description: {
      de: 'Erstellen Sie maßgeschneiderte Business-Apps ohne aufwändige Entwicklung — schnell, skalierbar und nahtlos integriert.',
      en: 'Build custom business apps without extensive development — fast, scalable and seamlessly integrated.',
      ar: 'أنشئ تطبيقات أعمال مخصصة دون تطوير مكثف — سريعة وقابلة للتوسع ومتكاملة بسلاسة.',
    },
    useCases: {
      de: ['Auftragsmanagement', 'Inventarverwaltung', 'Mitarbeiter-Apps', 'Kundenportale'],
      en: ['Order management', 'Inventory management', 'Employee apps', 'Customer portals'],
      ar: ['إدارة الطلبات', 'إدارة المخزون', 'تطبيقات الموظفين', 'بوابات العملاء'],
    },
  },
  {
    name: 'Power Automate',
    color: '#0066FF',
    image: '/new_images/AdobeStock_302630798.jpeg',
    description: {
      de: 'Automatisieren Sie repetitive Prozesse und verbinden Sie Ihre Tools — von E-Mail bis ERP — ohne eine Zeile Code.',
      en: 'Automate repetitive processes and connect your tools — from email to ERP — without a single line of code.',
      ar: 'أتمتة العمليات المتكررة وربط أدواتك — من البريد الإلكتروني إلى ERP — دون أي سطر كود.',
    },
    useCases: {
      de: ['Genehmigungs-Workflows', 'E-Mail-Automatisierung', 'Daten-Synchronisierung', 'Benachrichtigungen'],
      en: ['Approval workflows', 'Email automation', 'Data synchronization', 'Notifications'],
      ar: ['سير عمل الموافقة', 'أتمتة البريد الإلكتروني', 'مزامنة البيانات', 'الإشعارات'],
    },
  },
  {
    name: 'Power Virtual Agents',
    color: '#00B294',
    image: '/new_images/AdobeStock_343004004.jpeg',
    description: {
      de: 'Intelligente Chatbots für Kundenservice und interne Prozesse — erstellt ohne Programmierkenntnisse.',
      en: 'Intelligent chatbots for customer service and internal processes — built without coding knowledge.',
      ar: 'روبوتات محادثة ذكية لخدمة العملاء والعمليات الداخلية — مبنية دون معرفة برمجية.',
    },
    useCases: {
      de: ['24/7 Kundenservice', 'FAQ-Automatisierung', 'Lead-Qualifizierung', 'IT-Helpdesk'],
      en: ['24/7 customer service', 'FAQ automation', 'Lead qualification', 'IT helpdesk'],
      ar: ['خدمة عملاء 24/7', 'أتمتة الأسئلة الشائعة', 'تأهيل العملاء المحتملين', 'مكتب مساعدة تقنية'],
    },
  },
  {
    name: 'Power Pages',
    color: '#E8A800',
    image: '/new_images/pexels-photographwithart-16343598.jpg',
    description: {
      de: 'Professionelle Business-Webportale mit sicherem Datenzugang — für Kunden, Partner und Mitarbeiter.',
      en: 'Professional business web portals with secure data access — for customers, partners and employees.',
      ar: 'بوابات ويب أعمال احترافية مع وصول آمن للبيانات — للعملاء والشركاء والموظفين.',
    },
    useCases: {
      de: ['Kundenportale', 'Partner-Extranets', 'Self-Service-Portale', 'Event-Registrierung'],
      en: ['Customer portals', 'Partner extranets', 'Self-service portals', 'Event registration'],
      ar: ['بوابات العملاء', 'شبكات الشركاء', 'بوابات الخدمة الذاتية', 'تسجيل الفعاليات'],
    },
  },
];

const WHY = {
  de: ['Microsoft Partner mit zertifizierten Experten', 'Individuelle Lösungen für Ihren Prozess', 'Schnelle Implementierung & Training', 'Nahtlose Integration in bestehende Systeme', 'Langfristiger Support & Wartung'],
  en: ['Microsoft partner with certified experts', 'Custom solutions for your process', 'Fast implementation & training', 'Seamless integration into existing systems', 'Long-term support & maintenance'],
  ar: ['شريك Microsoft مع خبراء معتمدين', 'حلول مخصصة لعمليتك', 'تنفيذ وتدريب سريع', 'تكامل سلس مع الأنظمة الحالية', 'دعم وصيانة طويلة الأمد'],
};

interface AppointmentForm {
  name: string; email: string; phone: string; company: string; preferred_date: string; message: string;
}
const EMPTY: AppointmentForm = { name: '', email: '', phone: '', company: '', preferred_date: '', message: '' };

const PowerPlatformPage: React.FC = () => {
  const { currentLanguage, t } = useLanguage();
  const lang = currentLanguage as 'de' | 'en' | 'ar';
  const [form, setForm] = useState<AppointmentForm>(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError('');
    try {
      saveContact({
        name: form.name, email: form.email, phone: form.phone, company: form.company,
        message: `[Power Platform Anfrage]\nWunschdatum: ${form.preferred_date}\n\n${form.message}`,
      });
      setSubmitted(true);
      setForm(EMPTY);
    } catch {
      setSubmitError(t('contact.error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
      <SEOHead
        title={t('seo.power_platform_title')}
        description={t('seo.power_platform_description')}
        canonicalPath="/power-platform"
      />
      {/* ── HERO ─────────────────────────────────────────────────────── */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 100%)',
          pt: { xs: 10, md: 16 },
          pb: { xs: 10, md: 16 },
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {/* decorative grid lines */}
        <Box sx={{
          position: 'absolute', inset: 0, opacity: 0.04,
          backgroundImage: 'linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }} />

        <Container maxWidth="lg" sx={{ position: 'relative' }}>
          <MotionBox initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.4)', letterSpacing: '4px', mb: 3, display: 'block' }}>
              Microsoft Power Platform
            </Typography>
            <Typography
              variant="h1"
              sx={{
                color: 'white', fontWeight: 800, letterSpacing: '-2px',
                fontSize: { xs: '2.8rem', sm: '3.8rem', md: '5.5rem' },
                lineHeight: 1.0, mb: 4, maxWidth: 800,
              }}
            >
              {lang === 'de' ? 'Automatisierung. Analyse. Apps.' : lang === 'en' ? 'Automate. Analyze. Build.' : 'أتمتة. تحليل. تطبيقات.'}
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '1.2rem', lineHeight: 1.8, maxWidth: 580, mb: 6 }}
            >
              {lang === 'de'
                ? 'Wir implementieren das vollständige Microsoft Power Platform Ökosystem für Ihr Unternehmen — von Datenanalyse bis zur Prozessautomatisierung.'
                : lang === 'en'
                ? "We implement the full Microsoft Power Platform ecosystem for your business — from data analytics to process automation."
                : 'نقوم بتطبيق نظام Microsoft Power Platform البيئي الكامل لعملك — من تحليل البيانات إلى أتمتة العمليات.'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                href="#appointment"
                endIcon={<CalendarMonthIcon />}
                sx={{ bgcolor: 'white', color: '#0a0a0a', px: 4, py: 1.75, fontSize: '1rem', '&:hover': { bgcolor: '#f0f0f0' } }}
              >
                {lang === 'de' ? 'Kostenlos beraten lassen' : lang === 'en' ? 'Get free consultation' : 'احصل على استشارة مجانية'}
              </Button>
              <Button
                component={RouterLink} to="/services"
                variant="outlined"
                size="large"
                sx={{ borderColor: 'rgba(255,255,255,0.3)', color: 'white', px: 4, py: 1.75, fontSize: '1rem', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.06)' } }}
              >
                {lang === 'de' ? 'Alle Services' : lang === 'en' ? 'All Services' : 'جميع الخدمات'}
              </Button>
            </Box>
          </MotionBox>

          {/* Tool name pills */}
          <MotionBox
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.4 }}
            sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 8 }}
          >
            {TOOLS.map((tool) => (
              <Box
                key={tool.name}
                sx={{
                  px: 2, py: 0.75, borderRadius: 6,
                  border: '1px solid rgba(255,255,255,0.12)',
                  bgcolor: 'rgba(255,255,255,0.05)',
                  backdropFilter: 'blur(8px)',
                  display: 'flex', alignItems: 'center', gap: 1,
                }}
              >
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: tool.color }} />
                <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', fontWeight: 500 }}>
                  {tool.name}
                </Typography>
              </Box>
            ))}
          </MotionBox>
        </Container>
      </Box>

      {/* ── TOOLS ────────────────────────────────────────────────────── */}
      {TOOLS.map((tool, i) => (
        <Box
          key={tool.name}
          sx={{ bgcolor: i % 2 === 0 ? '#ffffff' : '#f8f8f8', py: { xs: 10, md: 14 }, borderBottom: '1px solid #e8e8e8' }}
        >
          <Container maxWidth="lg">
            <Grid
              container
              spacing={8}
              alignItems="center"
              direction={i % 2 === 0 ? 'row' : 'row-reverse'}
            >
              {/* Image */}
              <Grid item xs={12} md={5}>
                <MotionBox
                  initial={{ opacity: 0, x: i % 2 === 0 ? -30 : 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.7 }}
                  viewport={{ once: true }}
                >
                  <Box sx={{ position: 'relative' }}>
                    <Box
                      component="img"
                      src={tool.image}
                      alt={tool.name}
                      sx={{ width: '100%', height: 320, objectFit: 'cover', borderRadius: 3, display: 'block' }}
                    />
                    {/* color accent bar */}
                    <Box sx={{
                      position: 'absolute', bottom: -6, left: 24, right: 24, height: 6,
                      bgcolor: tool.color, borderRadius: '0 0 6px 6px',
                    }} />
                  </Box>
                </MotionBox>
              </Grid>

              {/* Content */}
              <Grid item xs={12} md={7}>
                <MotionBox
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  viewport={{ once: true }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: tool.color, flexShrink: 0 }} />
                    <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: '3px' }}>
                      Microsoft Power Platform
                    </Typography>
                  </Box>
                  <Typography variant="h2" sx={{ mb: 3, fontSize: { xs: '2rem', md: '2.75rem' } }}>
                    {tool.name}
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#555', lineHeight: 1.85, mb: 4, fontSize: '1.05rem' }}>
                    {tool.description[lang]}
                  </Typography>

                  <Grid container spacing={1.5}>
                    {tool.useCases[lang].map((uc) => (
                      <Grid item xs={12} sm={6} key={uc}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <CheckCircleIcon sx={{ fontSize: 18, color: tool.color, flexShrink: 0 }} />
                          <Typography variant="body2" sx={{ fontWeight: 500, color: '#333' }}>{uc}</Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </MotionBox>
              </Grid>
            </Grid>
          </Container>
        </Box>
      ))}

      {/* ── WHY US + FORM ────────────────────────────────────────────── */}
      <Box id="appointment" sx={{ bgcolor: '#ffffff', py: { xs: 10, md: 16 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={8}>
            {/* Why */}
            <Grid item xs={12} md={6}>
              <MotionBox initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }}>
                <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: '3px', mb: 2, display: 'block' }}>
                  TOP BHS
                </Typography>
                <Typography variant="h2" sx={{ mb: 5, fontSize: { xs: '2rem', md: '2.75rem' } }}>
                  {lang === 'de' ? 'Warum wir?' : lang === 'en' ? 'Why us?' : 'لماذا نحن؟'}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mb: 5 }}>
                  {WHY[lang].map((item) => (
                    <Box key={item} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <CheckCircleIcon sx={{ color: '#111', fontSize: 22, flexShrink: 0 }} />
                      <Typography variant="body1" sx={{ fontWeight: 500, color: '#222' }}>{item}</Typography>
                    </Box>
                  ))}
                </Box>
                <Divider sx={{ mb: 4 }} />
                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.8, mb: 4 }}>
                  {lang === 'de'
                    ? 'Als zertifizierter Microsoft-Partner bieten wir Ihnen umfassende Power Platform Lösungen — von der Konzeption über die Implementierung bis zum laufenden Support.'
                    : lang === 'en'
                    ? 'As a certified Microsoft partner, we offer comprehensive Power Platform solutions — from concept through implementation to ongoing support.'
                    : 'بوصفنا شريكًا معتمدًا من Microsoft، نقدم حلول Power Platform شاملة — من المفهوم إلى التنفيذ والدعم المستمر.'}
                </Typography>
                <Button component={RouterLink} to="/kooperation" variant="outlined" endIcon={<ArrowForwardIcon />}>
                  {lang === 'de' ? 'Unsere Projekte ansehen' : lang === 'en' ? 'View our projects' : 'عرض مشاريعنا'}
                </Button>
              </MotionBox>
            </Grid>

            {/* Form */}
            <Grid item xs={12} md={6}>
              <MotionBox
                initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }}
                sx={{ bgcolor: '#0a0a0a', borderRadius: 3, p: { xs: 4, md: 5 } }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                  <CalendarMonthIcon sx={{ fontSize: 22, color: 'rgba(255,255,255,0.6)' }} />
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'white' }}>
                    {lang === 'de' ? 'Kostenlose Beratung' : lang === 'en' ? 'Free Consultation' : 'استشارة مجانية'}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.45)', mb: 4 }}>
                  {lang === 'de' ? 'Power Platform · Microsoft 365 Lösungen' : lang === 'en' ? 'Power Platform · Microsoft 365 Solutions' : 'Power Platform · حلول Microsoft 365'}
                </Typography>

                {submitted ? (
                  <Alert severity="success" sx={{ borderRadius: 2, bgcolor: 'rgba(255,255,255,0.08)', color: 'white', '& .MuiAlert-icon': { color: '#6ee7b7' } }}>
                    {t('contact.success')}
                  </Alert>
                ) : (
                  <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {[
                      { name: 'name', label: t('contact.name'), type: 'text', required: true },
                      { name: 'email', label: t('contact.email'), type: 'email', required: true },
                      { name: 'phone', label: t('contact.phone'), type: 'tel', required: false },
                      { name: 'company', label: t('contact.company'), type: 'text', required: false },
                    ].map((f) => (
                      <TextField
                        key={f.name} label={f.label} name={f.name} type={f.type}
                        value={form[f.name as keyof AppointmentForm]}
                        onChange={handleChange} required={f.required} fullWidth size="small"
                        sx={{
                          '& .MuiOutlinedInput-root': { color: 'white', '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' }, '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' }, '&.Mui-focused fieldset': { borderColor: 'white' } },
                          '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' },
                          '& .MuiInputLabel-root.Mui-focused': { color: 'white' },
                        }}
                      />
                    ))}
                    <TextField
                      label={lang === 'de' ? 'Wunschdatum' : lang === 'en' ? 'Preferred Date' : 'التاريخ المفضل'}
                      name="preferred_date" type="date"
                      value={form.preferred_date} onChange={handleChange}
                      fullWidth size="small" InputLabelProps={{ shrink: true }}
                      inputProps={{ min: new Date().toISOString().split('T')[0] }}
                      sx={{
                        '& .MuiOutlinedInput-root': { color: 'white', colorScheme: 'dark', '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' }, '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' }, '&.Mui-focused fieldset': { borderColor: 'white' } },
                        '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' },
                        '& .MuiInputLabel-root.Mui-focused': { color: 'white' },
                      }}
                    />
                    <TextField
                      label={t('contact.message')} name="message"
                      value={form.message} onChange={handleChange}
                      multiline rows={3} fullWidth size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': { color: 'white', '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' }, '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' }, '&.Mui-focused fieldset': { borderColor: 'white' } },
                        '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' },
                        '& .MuiInputLabel-root.Mui-focused': { color: 'white' },
                      }}
                    />
                    {submitError && <Alert severity="error" sx={{ borderRadius: 2 }}>{submitError}</Alert>}
                    <Button
                      type="submit" variant="contained" fullWidth disabled={submitting}
                      endIcon={<ArrowForwardIcon />}
                      sx={{ mt: 1, bgcolor: 'white', color: '#0a0a0a', py: 1.5, fontSize: '0.95rem', '&:hover': { bgcolor: '#f0f0f0' }, '&:disabled': { bgcolor: 'rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.4)' } }}
                    >
                      {submitting ? t('common.loading') : lang === 'de' ? 'Anfrage senden' : lang === 'en' ? 'Send Request' : 'إرسال الطلب'}
                    </Button>
                  </Box>
                )}
              </MotionBox>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default PowerPlatformPage;
